import { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import {
	ESubscriptionType,
	ISubscriptionOffer,
	ISubscriptionOffering,
} from '@mopla/data-models';
import {
	EMlpIconTypes,
	EMplVariantTypes,
	MplIconMessage,
	MplRadioButton,
} from '@mopla/ui';
import { getDTicketType } from '@mopla/utils';

interface IStep1JobticketContentProps {
	subscriptionOffering: ISubscriptionOffering | null;
	selectedMonth: string | null;
	onSelectMonth: (month: string) => void;
	showValidityWarning: boolean;
	daysRemaining: number;
	subscriptionType?: ESubscriptionType;
}

export const Step1JobticketContent: FC<IStep1JobticketContentProps> = ({
	subscriptionOffering,
	selectedMonth,
	onSelectMonth,
	showValidityWarning,
	daysRemaining,
	subscriptionType,
}) => {
	const { t } = useTranslation('subscribeDTicket');
	const ticketType = getDTicketType(subscriptionType);
	/**
	 * Discounted D-Ticket uses a plain "Abonnement" heading (per Figma) instead
	 * of the "{{ticketType}}-Abonnement" form used by the other jobticket types.
	 */
	const isDiscounted = subscriptionType === ESubscriptionType.DISCOUNTED;

	const offers = useMemo(
		() => subscriptionOffering?.offers ?? [],
		[subscriptionOffering]
	);

	const currentMonthName = useMemo(() => {
		if (!selectedMonth) return '';
		return dayjs(selectedMonth).format('MMMM');
	}, [selectedMonth]);

	return (
		<Stack gap={2}>
			<Stack gap={0.5}>
				<Typography variant="h4-sb" color="primary.main">
					{isDiscounted
						? t('step1_jobticket.title_discounted')
						: t('step1_jobticket.title', { ticketType })}
				</Typography>
				<Typography variant="l1" color="solutions.600">
					{t('step1_jobticket.description', { ticketType })}
				</Typography>
			</Stack>

			<Stack gap={2}>
				{offers.map((offer: ISubscriptionOffer) => {
					const dateValue = offer.startDate;
					const label = t('step1_jobticket.offer_label', {
						date: dayjs(dateValue).format('01. MMMM YYYY'),
					});

					return (
						<MplRadioButton
							key={dateValue}
							dataTestId={`subscribeDTicket-monthOption-${dateValue}`}
							text={label}
							checked={selectedMonth === dateValue}
							onChange={() => onSelectMonth(dateValue)}
							value={dateValue}
						/>
					);
				})}
			</Stack>
			{showValidityWarning && (
				<MplIconMessage
					type={EMlpIconTypes.Warning}
					variant={EMplVariantTypes.Warning}
					message={
						<Trans
							i18nKey="subscribeDTicket:step1.validity_warning"
							values={{
								days: daysRemaining,
								month: currentMonthName,
							}}
							components={{
								bold: <strong />,
							}}
						/>
					}
				/>
			)}
		</Stack>
	);
};
